import React from 'react'
import { FormProvider } from 'react-hook-form'
import Typography from '@mui/material/Typography'

// Component Imports
import Link from '@components/Link'

// Component Imports
import { InputAdornment, Box, TextField, IconButton } from '@mui/material'
import { ChangeEvent, useRef } from 'react'
import FormInput from '@/components/form/FormInput'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTogglePinMutation } from '@/@data'
import { ErrorAlert } from '@/components/common/ErrorAlert'
import { useTranslator } from '@/hooks/useTranslator'
import DirectionalIcon from '@components/theme/DirectionalIcon'
import ForgotPasswordStepHeader from '@/components/auth/forgot-password/ForgotPasswordStepHeader'
import ForgotPasswordFormStepAction from '@/components/auth/forgot-password/ForgotPasswordFormStepAction'
import { useForgotPasswordStepForm } from '@/hooks/useForgotPasswordStepForm'
import { setAuthenticaSuccess } from '@/redux-store/slices/authenticaSuccess'
import { useAppDispatch } from '@/redux-store'
import useMediaQuery from '@mui/material/useMediaQuery';


const ForgotPassword = () => {

  const { __ } = useTranslator()

  const handleClose = () => {
    methods.reset()
  }

  const dispatch = useAppDispatch()

  const handleResetPasswordSuccess = () => {
    setActiveStep(0)
    dispatch(
      setAuthenticaSuccess({
        title: __('Reset Password successful'),
        description: __(
          'You have changed your password successfully, please wait a moment and you will be redirected to the login page.'
        ),
        desButton: __('Waiting back to login'),
        isOpen: true
      })
    )
  }

  const {
    methods,
    activeStep,
    setActiveStep,
    handleNext,
    handleBack,
    handleSubmit,
    loadingForm,
    isStepValid
  } = useForgotPasswordStepForm(handleResetPasswordSuccess)

  const isMobile = useMediaQuery('(max-width: 768px)');

  const steps = [
    {
      labelPC: __('Enter Email'),
      labelMB: __('Step 01'),
    },
    {
      labelPC: __('Verify Account'),
      labelMB: __('Step 02'),
    },
    {
      labelPC: __('Set Password'),
      labelMB: __('Step 03'),
    }
  ]


  const methodOTP = useForm<{
    pin: string
  }>({
    defaultValues: {
      pin: ''
    },
    mode: 'onChange',
    resolver: yupResolver(
      yup.object().shape({
        pin: yup
          .string()
          .required(__('Authentication Code is required'))
          .min(6, __('Code must be 6 characters'))
          .max(6, __('Code must be 6 characters'))
      })
    )
  })

  const togglePinMutation = useTogglePinMutation()

  const inputRefs = useRef<HTMLInputElement[]>([])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const value = e.target.value
    const currentPin = methodOTP.getValues('pin') || ''
    const newPin = currentPin.split('')

    newPin[index] = value

    methodOTP.setValue('pin', newPin.join(''), { shouldValidate: true })

    methods.setValue('verification_code', newPin.join(''), { shouldValidate: true })

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'Backspace' && !(e.target as HTMLInputElement).value && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  // step 3

  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowConfirmPassword] = useState(false)

  const handleClickShowPassword = () => setShowPassword(show => !show)

  const password = methods.watch('password') || '';
  const passwordConfirm = methods.watch('confirm_password') || '';

  return (
    <div className='flex flex-col  gap-6 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset] mbs-11 mbe-11 sm:mbs-14 md:mbs-0 md:mbe-0 bs-full md:min-bs-[80dvh] min-bs-[80dvh] md:px-0 px-5'>
      <Typography className='flex' color='primary.main'>
        <Link
          href='/cms/login'
          className='flex items-center gap-1.5'
        >
          <DirectionalIcon
            ltrIconClass='tabler-chevron-left'
            rtlIconClass='tabler-chevron-right'
            className='text-xl'
          />
          <span>{__('Back to login')}</span>
        </Link>
      </Typography>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <ForgotPasswordStepHeader
            steps={steps.map(step => ({ label: isMobile ? step.labelMB : step.labelPC }))}
            activeStep={activeStep}
          />

          <div className='flex flex-col gap-1 align-center justify-center items-center pt-[30px]'>
            <img src='/images/pages/lift-logo.png' alt='' width={100} height={46} />
            <Typography variant='h4' className='md:text-[40px] text-[32px] leading-[38px] font-bold mt-8'>{__('Forgot Password')}</Typography>
            <Typography className='md:text-[15px] text-[13px] leading-[22px] text-[##2F2B3DB2]'>{__('Enter your email and we will send you instructions')}</Typography>
            <Typography className='md:text-[15px] text-[13px] leading-[22px] text-[##2F2B3DB2]'>{__('to reset your password')}</Typography>
          </div>

          <div className='flex flex-col gap-1 mt-5'>


            {activeStep === 0 && (
              <FormInput
                id='email'
                placeholder={__('Email Address')}
                name='email'
                control={methods.control}
                errorMessage={methods.formState.errors.email?.message}
                className='rounded-full'
                slotProps={{
                  input: {
                    style: {padding: '5px 15px', borderRadius: '50px'},
                    startAdornment: (
                      <InputAdornment position='start'>
                        <i className='tabler-mail' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                      </InputAdornment>
                    )
                  }
                }}
              />
            )}
            {activeStep === 1 && (
              <div className='flex flex-col gap-2 w-full'>
                <Typography className='text-[15px] font-medium leading-[22x] not-italic text-[rgba(47,43,61,0.7)]'>
                  {__('Type your 6 digit security code')}
                </Typography>
                <ErrorAlert error={togglePinMutation.error} onClose={() => togglePinMutation.reset()} />

                <Box display='flex' gap={4} mb={2} style={{ width: '100%' }} className='justify-center'>
                  {[0, 1, 2, 3, 4, 5].map((_, index) => (
                    <TextField
                      key={index}
                      type='number'
                      inputMode='numeric'
                      sx={{
                        '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                          textAlign: 'center',
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield'
                        },
                        '& input': {
                          textAlign: 'center'
                        }
                      }}
                      slotProps={{
                        input: {
                          className: 'otp-input',
                          inputMode: 'numeric',
                          onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
                            let val = e.target.value

                            val = val.replace(/-/g, '')

                            if (val.length > 1) {
                              val = val.slice(0, 1)
                            }

                            e.target.value = val
                          }
                        }
                      }}
                      variant='outlined'
                      onChange={e => handleChange(e, index)}
                      onKeyDown={e => handleKeyDown(e, index)}
                      inputRef={el => (inputRefs.current[index] = el)}
                    />
                  ))}
                </Box>

                <Typography className='text-[15px] text-center font-medium leading-[22x] not-italic text-[rgba(47,43,61,0.7)]'>
                  <span>{__("Didn't get the mail? ")}</span>
                  <Link href='/cms' className='text-[#6F83F6] text-[15px]'>
                    {__('Resend')}
                  </Link>
                </Typography>
              </div>
            )}
            {activeStep === 2 && (
              <Box className='flex flex-col gap-4 '>
                <FormInput
                  className='rounded-full'
                  type={showPassword ? 'text' : 'password'}
                  id='outlined-adornment-password'
                  placeholder={__('Password')}
                  name='password'
                  control={methods.control}
                  errorMessage={methods.formState.errors.password?.message}
                  slotProps={{
                    input: {
                      style: {padding: '5px 15px', borderRadius: '50px'},
                      startAdornment: (
                        <InputAdornment position='start'>
                          <i className='tabler-lock' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          {methods.watch('password') && (
                            <IconButton size='small' onClick={() => methods.setValue('password', '')} tabIndex={-1}>
                              <i className='tabler-circle-x' />
                            </IconButton>
                          )}
                          <IconButton
                            edge='end'
                            onClick={handleClickShowPassword}
                            onMouseDown={e => e.preventDefault()}
                          >
                            <i className={showPassword ? 'tabler-eye-off' : 'tabler-eye'} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />
                <FormInput
                  className='rounded-full'
                  type={showPasswordConfirm ? 'text' : 'password'}
                  id='outlined-adornment-password-confirm'
                  placeholder={__('Confirm Password')}
                  name='confirm_password'
                  control={methods.control}
                  errorMessage={methods.formState.errors.confirm_password?.message}
                  slotProps={{
                    input: {
                      style: {padding: '5px 15px', borderRadius: '50px'},
                      startAdornment: (
                        <InputAdornment position='start'>
                          <i className='tabler-lock' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          {passwordConfirm !== '' && (
                            <IconButton size='small' onClick={() => methods.setValue('confirm_password', '')} tabIndex={-1}>
                              <i className='tabler-circle-x' />
                            </IconButton>
                          )}
                          <IconButton
                            edge='end'
                            onClick={() => setShowConfirmPassword(show => !show)}
                            onMouseDown={e => e.preventDefault()}
                          >
                            <i className={showPasswordConfirm ? 'tabler-eye-off' : 'tabler-eye'} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />

                <Box className='flex flex-col gap-2 mb-4'>
                  <Box display='flex' alignItems='center' gap={1}>
                    <Box className='w-[10px] h-[10px] rounded-full ' bgcolor={/[0-9]/.test(password) ? '#197049' : '#e0e0e0'} />
                    <Typography className='text-[13px] leading-[20px]' color={/[0-9]/.test(password) ? '#197049' : '#4b465c'}>
                      {__('At least one numeral (0-9)')}
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center' gap={1}>
                    <Box className='w-[10px] h-[10px] rounded-full ' bgcolor={password.length >= 6 ? '#197049' : '#e0e0e0'} />
                    <Typography className='text-[13px]  leading-[20px]' color={password.length >= 6 ? '#197049' : '#4b465c'}>
                      {__('Minimum 6 characters')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </div>
          <ForgotPasswordFormStepAction
            handleBack={handleBack}
            handleNext={handleNext}
            handleSubmit={handleSubmit}
            steps={steps}
            activeStep={activeStep}
            handleClose={handleClose}
            loadingForm={loadingForm}
            isStepValid={isStepValid}
          />
        </form>
      </FormProvider>

      <div className='mt-auto text-center'>
        <span>{__('© 2025, Made with ❤️ by')}</span>  <span className='text-primary'>{__('DOSI GROUP')}</span>
      </div>
    </div>
  )
}

export default ForgotPassword
